import { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { money, monthLabel, downloadCSV } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export function MerchantDirectory({ company = "" }) {
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [feeF, setFeeF] = useState("");
  const [d, setD] = useState(null);
  const params = { ...(month ? { month } : {}), ...(year ? { year } : {}), ...(feeF ? { fee: feeF } : {}), ...(company ? { company } : {}) };
  useEffect(() => {
    let live = true;
    api.get("/merchant-directory", { params }).then((r) => live && setD(r.data));
    return () => { live = false; };
  }, [month, year, feeF, company]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = q.trim().toLowerCase();
  const list = (d?.merchants || []).filter((m) => !s || m.merchant_name.toLowerCase().includes(s) || m.merchant_id.toLowerCase().includes(s));
  const exportCsv = () => downloadCSV(`merchants-a-z${month ? `-${month}` : ""}${year ? `-${year}` : ""}.csv`, ["Merchant", "Merchant ID", "Belongs to", "Fee picked", "Month", "Residual", "YTD residual", ...(year ? [`${year} residual`] : []), "Lifetime residual", "Volume"],
    list.map((m) => [m.merchant_name, m.merchant_id, m.owner, m.picked_fee ?? "", m.month, m.residual, m.ytd_residual, ...(year ? [m.year_residual] : []), m.lifetime_residual, m.volume]));
  const exportPdf = async () => {
    try {
      const r = await api.get("/merchant-directory/pdf", { params: { ...params, ...(q.trim() ? { q: q.trim() } : {}) }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `merchants-a-z${month ? `-${month}` : ""}${year ? `-${year}` : ""}.pdf`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const controls = (
    <div className="flex gap-2">
      <Input data-testid="directory-search" placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-40 bg-[#0B0F17] text-xs" />
      <select data-testid="directory-month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2">
        <option value="">All months (latest)</option>
        {(d?.months || []).map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
      </select>
      <select data-testid="directory-fee" value={feeF} onChange={(e) => setFeeF(e.target.value)} className="h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2">
        <option value="">All fees</option>
        {[25, 89, 119, 139].map((f) => <option key={f} value={String(f)}>${f} picked</option>)}
        <option value="none">No fee picked</option>
      </select>
      <select data-testid="directory-year" value={year} onChange={(e) => setYear(e.target.value)} className="h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2">
        <option value="">Full-year column: off</option>
        {(d?.years || []).map((y) => <option key={y} value={y}>{y} full year</option>)}
      </select>
      <Button data-testid="directory-export" size="sm" variant="outline" onClick={exportCsv}><Download size={13} className="mr-1" />CSV</Button>
      <Button data-testid="directory-pdf" size="sm" variant="outline" onClick={exportPdf}><FileText size={13} className="mr-1" />PDF</Button>
    </div>
  );
  return (
    <Panel title={`Merchants A–Z${d ? ` · ${list.length}` : ""}`} action={controls}>
      {!d ? <Empty>Loading…</Empty> : list.length === 0 ? <Empty>No merchants found.</Empty> : (
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="tbl w-full" data-testid="merchant-directory-table">
            <thead><tr><th>Merchant</th><th>Merchant ID</th><th>Belongs to</th><th className="!text-right">Fee</th><th>Month</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th><th className="!text-right">YTD</th>{year && <th className="!text-right" data-testid="directory-year-header">{year}</th>}<th className="!text-right">Lifetime</th></tr></thead>
            <tbody>{list.map((m) => (
              <tr key={m.merchant_id} data-testid={`directory-row-${m.merchant_id}`}>
                <td>{m.merchant_name || "—"}</td>
                <td className="font-mono text-xs text-slate-400">{m.merchant_id}</td>
                <td className="text-slate-300">{m.owner}</td>
                <td className="text-right font-mono text-xs" data-testid={`directory-fee-${m.merchant_id}`}>{m.picked_fee != null ? <span className="text-emerald-300">{money(m.picked_fee)}</span> : <span className="text-slate-600">—</span>}</td>
                <td className="font-mono text-xs">{monthLabel(m.month)}</td>
                <td className="text-right font-mono text-slate-400">{money(m.volume)}</td>
                <td className={`text-right font-mono ${m.residual < 0 ? "text-rose-400" : "text-emerald-400"}`}>{money(m.residual)}</td>
                <td className={`text-right font-mono ${m.ytd_residual < 0 ? "text-rose-400" : "text-slate-200"}`} data-testid={`directory-ytd-${m.merchant_id}`}>{money(m.ytd_residual)}</td>
                {year && <td className={`text-right font-mono ${m.year_residual < 0 ? "text-rose-400" : "text-blue-300"}`} data-testid={`directory-year-${m.merchant_id}`}>{money(m.year_residual)}</td>}
                <td className={`text-right font-mono ${m.lifetime_residual < 0 ? "text-rose-400" : "text-slate-200"}`} data-testid={`directory-lifetime-${m.merchant_id}`}>{money(m.lifetime_residual)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
